/**
 * A role value. Which roles exist comes only from the environment's AUTH_ROLES (the app roles, with the labels and
 * user-assignability `resources.auth.config.roles` gives them): see {@link roleDefinitions}.
 */
export type Role = string;

/**
 * An app role from AUTH_ROLES: the environment's Azure app registration roles, injected on deploy. The roles listed in
 * `resources.auth.config.roles` also have their label and whether users can be given them.
 */
export interface AuthRole {
  readonly id: string;
  readonly value: string;
  readonly label?: string;
  readonly userAssignable?: boolean;
  readonly displayName?: string;
  readonly description?: string;
  /** "User" when the role can be given to users, "Application" when it can be given to API keys and apps. */
  readonly allowedMemberTypes?: readonly string[];
  readonly isEnabled?: boolean;
}

export interface RoleDefinition {
  readonly value: Role;
  readonly label: string;
  readonly userAssignable: boolean;
}

/** A role and its human-readable label. */
export interface RoleOption {
  readonly value: Role;
  readonly label: string;
}

/** AUTH_ROLES as injected (a JSON string) or already parsed. */
export type AuthRolesInput = string | readonly AuthRole[] | null | undefined;

// Everyone signed in has `user`, so it isn't offered.
const IMPLICIT_ROLES = new Set(["user"]);
// Roles that aren't app roles: `owner` is ownership of the tenancy's group, which the auth service sets instead.
const NON_APP_ROLES: readonly AuthRole[] = [{ id: "owner", value: "owner", label: "Owner", userAssignable: true }];
const LABELS: Record<string, string> = { superadmin: "Super Admin" };

/** A role's label, e.g. "Timesheet Writer All" for `timesheet-writer-all`. */
export const roleLabel = (value: Role): string =>
  LABELS[value] ??
  value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** Missing configuration is allowed; supplied configuration must be a non-empty app-role array. */
export const parseAuthRoles = (authRoles: AuthRolesInput): readonly AuthRole[] => {
  if (authRoles == null) return [];
  let parsed: unknown;
  try {
    parsed = typeof authRoles === "string" ? JSON.parse(authRoles) : authRoles;
  } catch {
    throw new Error("AUTH_ROLES must be a non-empty array of app roles");
  }
  if (
    !Array.isArray(parsed) ||
    parsed.length === 0 ||
    !parsed.every(
      (role) =>
        role &&
        typeof role.id === "string" &&
        typeof role.value === "string" &&
        role.value.length > 0 &&
        (role.label === undefined || typeof role.label === "string") &&
        (role.userAssignable === undefined || typeof role.userAssignable === "boolean") &&
        (role.isEnabled === undefined || typeof role.isEnabled === "boolean") &&
        (role.allowedMemberTypes === undefined ||
          (Array.isArray(role.allowedMemberTypes) &&
            role.allowedMemberTypes.every((member: unknown) => typeof member === "string"))),
    )
  )
    throw new Error("AUTH_ROLES must be a non-empty array of app roles");
  return parsed;
};

/**
 * The environment's roles listed in `resources.auth.config.roles` (those AUTH_ROLES gives a label), plus `owner`, by
 * label. Until AUTH_ROLES has labels, every enabled app role is offered, to users when its app role allows users. With
 * no AUTH_ROLES there are no roles.
 */
export const roleDefinitions = (authRoles: AuthRolesInput): RoleDefinition[] => {
  const appRoles = parseAuthRoles(authRoles).filter(
    ({ value, isEnabled }) => value && isEnabled !== false && !IMPLICIT_ROLES.has(value),
  );
  if (appRoles.length === 0) return [];
  const listed = appRoles.some(({ label }) => label) ? appRoles.filter(({ label }) => label) : appRoles;
  return [...listed, ...NON_APP_ROLES.filter(({ value }) => !listed.some((role) => role.value === value))]
    .map(({ value, label, userAssignable, allowedMemberTypes }) => ({
      value,
      label: label ?? roleLabel(value),
      userAssignable: userAssignable ?? (allowedMemberTypes ?? []).includes("User"),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
};

/** The role lists for an environment's AUTH_ROLES. */
export interface RoleLists {
  ROLE_DEFINITIONS: RoleDefinition[];
  ALL_ROLES: Role[];
  USER_ROLES: Role[];
  USER_ROLE_OPTIONS: RoleOption[];
  API_KEY_ROLE_OPTIONS: RoleOption[];
}

export const roleListsFor = (authRoles: AuthRolesInput): RoleLists => {
  const definitions = roleDefinitions(authRoles);
  const userAssignable = definitions.filter(({ userAssignable }) => userAssignable);
  return {
    ROLE_DEFINITIONS: definitions,
    /** Every role value in the environment. */
    ALL_ROLES: definitions.map(({ value }) => value),
    /** Roles that can be given to an interactive user. */
    USER_ROLES: userAssignable.map(({ value }) => value),
    /** User-assignable role values and labels. */
    USER_ROLE_OPTIONS: userAssignable.map(({ value, label }): RoleOption => ({ value, label })),
    /** API-key role values and labels: every role, including key-only roles. */
    API_KEY_ROLE_OPTIONS: definitions.map(({ value, label }): RoleOption => ({ value, label })),
  };
};

/**
 * AUTH_ROLES where this is running: the portal's `window.__ENV__` (public/env.js, which loads before the app), or
 * Node's `process.env` in services.
 */
export const injectedAuthRoles = (): string | undefined => {
  const runtime = globalThis as typeof globalThis & {
    window?: { __ENV__?: { AUTH_ROLES?: string } };
    process?: { env?: { AUTH_ROLES?: string } };
  };
  return runtime.window?.__ENV__?.AUTH_ROLES ?? runtime.process?.env?.AUTH_ROLES;
};

/** This environment's roles, from its AUTH_ROLES. Empty when it has none (e.g. in tests). */
export const { ROLE_DEFINITIONS, ALL_ROLES, USER_ROLES, USER_ROLE_OPTIONS, API_KEY_ROLE_OPTIONS } = roleListsFor(
  injectedAuthRoles(),
);
